import React, { useState, useEffect, useCallback } from "react";
import { PropTypes } from "prop-types";
import { Label } from "components/core";
import { AlertCircleIcon } from "lucide-react";
import { isNullEmptyOrWhitespace } from "helpers/stringUtilities";
import useCalcTrigger from "hooks/useCalcTrigger";

const TextArea = ({
  label,
  id,
  hint,
  validate,
  value,
  setValue,
  setValid,
  required = true,
  isFullWidth = false,
  disabled = false,
  labelPosition = "top",
  addonLeft,
  addonRight,
  rows = 3,
  dependencies = [],
  defaultValue,
  theme,
  render,
  labelSize,
  disableCalcTrigger,
  ...other
}) => {
  useCalcTrigger(value, setValue, disableCalcTrigger);

  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hasValue, setHasValue] = useState(value?.length > 0);

  const inputErrorClasses = ["border-danger-600"];
  const inputClasses = [
    "rounded-md pt-3 pb-3",
    "border-1",
    "focus:ring-4 focus:ring-offset-0",
    `${disabled ? "disabled:opacity-50" : ""}`,
  ];
  const errorIconClasses = [];

  // Theme
  if (theme === "darkgray") {
    inputClasses.push(
      "bg-gray-900 border-gray-500 text-gray-400 focus:border-gray-400 focus:ring-gray-800"
    );
    errorIconClasses.push("bg-gray-900");
  } else {
    inputClasses.push(
      "bg-white border-gray-300 focus:border-gray-700 focus:ring-gray-50"
    );
    errorIconClasses.push("bg-white");
  }

  //#region Callbacks

  /**
   * Set the input value.
   * @param {string} value  The new input value.
   */
  const setInputValue = useCallback((value) => {
    // Prevent setting input to null or undefined
    // Controlled components should not have null or undefined value
    if (typeof value === "undefined" || value === null) return;

    setValue(value);
    setHasValue(value.length > 0);
  }, [setValue]);

  /**
   * Validate the input value.
   * @returns {string|null} The error message or null if valid.
   */
  const validation = () => {
    if (validate) return validate(value);
  };

  //#endregion

  //#region Side-effects

  /**
   * Trigger validation when value or dependencies change.
   * Useful when you wish to revalidate when related input changes.
   */
   useEffect(() => {
    const validationResult = validation();
    if (validationResult !== error) setError(validationResult);
    
    const complete = required && isNullEmptyOrWhitespace(value) ? false : true;
    if (setValid) setValid(!validationResult, value, required, complete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, value, required]);

  /**
   * Default value
   * Set default value here to trigger calculation parser,
   * otherwise calculation strings won't be evaluated.
   */
   useEffect(() => {
    if (touched || !defaultValue || !setInputValue) return;

    if (isNullEmptyOrWhitespace(value)) {
      setInputValue(defaultValue);
    }
  }, [value, defaultValue, touched, setInputValue]);

  //#endregion

  // Prevent dom element rendering
  if (render === false) {
    return null;
  }

  return (
    <div
      className={`${
        labelPosition === "left" ? "grid grid-cols-3 gap-4" : "relative"
      }`}
    >
      <Label
        id={id}
        text={label}
        required={required}
        focused={focused}
        hasValue={hasValue}
        position={labelPosition}
        theme={theme}
        erroneous={touched && error && !focused}
        size={labelSize}
      />
      <div className={`mt-1 ${labelPosition === "left" ? "col-span-2" : ""}`}>
        <div className="rounded-md shadow-sm relative">
          <textarea
            id={id}
            className={`block w-full tablet:text-sm ${inputClasses.join(" ")} ${
              touched && error && !focused ? inputErrorClasses.join(" ") : ""
            }`}
            value={value ?? ""}
            onBlur={() => {
              setTouched(true);
              setFocused(false);
            }}
            onChange={(ev) => setInputValue(ev.target.value)}
            onFocus={() => setFocused(true)}
            aria-invalid={error}
            disabled={disabled}
            rows={rows}
            {...other}
          />
          {touched && error && !focused && (
            <div className="absolute -top-2 right-2 pointer-events-none bg-white px-1">
              <AlertCircleIcon className="h-5 w-5 text-danger-500" />
            </div>
          )}
        </div>
        {touched && error && !focused ? (
          <p className="mt-2 text-xs text-danger-600" id="email-error">
            {error}
          </p>
        ) : (
          hint && (
            <p className="mt-2 text-xs text-gray-500" id="email-error">
              {hint}
            </p>
          )
        )}
      </div>
    </div>
  );
};

TextArea.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string.isRequired,
  hint: PropTypes.string,
  validate: PropTypes.func,
  value: PropTypes.string,
  setValue: PropTypes.func,
  setValid: PropTypes.func,
  labelPosition: PropTypes.string,
  rows: PropTypes.number,
  dependencies: PropTypes.array,
  render: PropTypes.bool,
};

export { TextArea };
